// Real data for Wolf Eye Drone Interceptor Module
// Based on actual product specifications from Labana Dynamics OU

export const productData = {
  name: "Wolf Eye",
  fullName: "Wolf Eye Modular Defense System",
  tagline: "Modular Defense Intelligence",
  description: "Autonomous aerial security system designed to detect, identify, and neutralize aerial and ground threats including drones, hostile vehicles, and organized assault groups.",

  hero: {
    headline: "Wolf Eye",
    subheadline: "Modular Defense Intelligence",
    description: "Next-generation modular autonomous system combining detection, identification, and precision neutralization capabilities for comprehensive threat protection.",
    imageUrl: "https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/sphp7fxv_Drone%20image%201%23.png"
  },

  specifications: {
    detection: [
    { label: "Module Capacity", value: "6-9 drones per unit" },
    { label: "Network Density", value: "~5 km spacing" },
    { label: "Operational Mode", value: "24/7 Autonomous" },
    { label: "Deployment Time", value: "<30 minutes" }],

    droneConfigs: [
    {
      type: "Interceptor Drone",
      speed: "300 km/h",
      flightTime: "7.25 min",
      range: "36 km"
    },
    {
      type: "High-Speed Interception",
      speed: "480 km/h",
      flightTime: "2 min",
      range: "15.3 km"
    },
    {
      type: "Urban Infrastructure",
      speed: "50 km/h",
      flightTime: "16 min",
      range: "13.3 km"
    }],

    operational: [
    { label: "Operator Requirements", value: "2 operators (city-scale)" },
    { label: "Environmental Protection", value: "Sealed modules" },
    { label: "Reconfiguration Time", value: "~30 minutes" },
    { label: "AI Decision-Making", value: "Autonomous under degraded comms" }]

  },

  features: [
  {
    icon: "Shield",
    title: "Modular Architecture",
    description: "Rapidly deployable, easily transportable modules with standardized drone components. Scale from critical infrastructure (5-20 modules) to city-wide protection (50-150 modules)."
  },
  {
    icon: "Target",
    title: "Multi-Threat Capability",
    description: "Detects and neutralizes aerial threats (drones), ground armor, and organized assault groups with specialized drone configurations for each threat type."
  },
  {
    icon: "Zap",
    title: "Autonomous Operation",
    description: "AI-driven decision logic enables operation even under degraded communication conditions. Minimal human oversight required with critical engagement authorization."
  },
  {
    icon: "Network",
    title: "Networked Defense",
    description: "Interconnected modules create comprehensive defense perimeter with automatic threat prioritization and optimal drone deployment across the network."
  },
  {
    icon: "Brain",
    title: "Electronic Warfare Resilience",
    description: "Additional protection measures against high-power microwave weapons and jamming. Maintains operational capability in contested electromagnetic environments."
  },
  {
    icon: "Radar",
    title: "24/7 All-Weather",
    description: "Continuous operation capability in day/night conditions. Advanced night operability systems under active development for complete environmental coverage."
  }],


  useCases: [
  {
    title: "Critical Infrastructure Protection",
    description: "Defend power plants, industrial facilities, and strategic installations with 5-20 module deployments providing comprehensive aerial and ground threat coverage.",
    scenarios: ["Energy sector facilities", "Strategic industrial sites", "Government installations", "Communication hubs"]
  },
  {
    title: "City-Scale Defense",
    description: "Protect entire metropolitan areas with 50-150 networked modules providing layered defense against drone swarms and coordinated attacks.",
    scenarios: ["Municipal infrastructure", "Urban population centers", "Administrative districts", "Public event venues"]
  },
  {
    title: "Military Base Security",
    description: "Provide autonomous perimeter defense for forward operating bases, command posts, and strategic military installations with minimal operator oversight.",
    scenarios: ["FOB protection", "Command center security", "Convoy staging areas", "Ammunition depots"]
  },
  {
    title: "Border & Checkpoint Security",
    description: "Monitor and secure border crossings, checkpoints, and restricted zones against unauthorized drone reconnaissance and hostile incursions.",
    scenarios: ["Border crossing points", "Security checkpoints", "Restricted airspace zones", "Patrol route coverage"]
  }],


  certifications: [
  { name: "Modular Design", description: "Standardized components for rapid reconfiguration", status: "Implemented" },
  { name: "EW Protection", description: "High-power microwave weapon resistance", status: "Active" },
  { name: "AI Autonomy", description: "Degraded communication operation capability", status: "Operational" },
  { name: "24/7 Operation", description: "Continuous day/night capability", status: "Active" },
  { name: "Multi-Threat", description: "Air, ground armor, and infantry threats", status: "Certified" },
  { name: "Scalable Network", description: "5-150+ module deployment capability", status: "Proven" }],


  comparison: {
    competitors: [
    {
      feature: "Deployment Time",
      wolfEye: "<30 min",
      competitorA: "2-4 hours",
      competitorB: "1-2 hours"
    },
    {
      feature: "Module Price",
      wolfEye: "€90,000",
      competitorA: "€250,000+",
      competitorB: "€180,000+"
    },
    {
      feature: "Autonomous Operation",
      wolfEye: "Yes (AI-driven)",
      competitorA: "Partial",
      competitorB: "Limited"
    },
    {
      feature: "Threat Types",
      wolfEye: "Air + Ground",
      competitorA: "Air only",
      competitorB: "Air only"
    },
    {
      feature: "Network Scalability",
      wolfEye: "5-150+ modules",
      competitorA: "Fixed install",
      competitorB: "Limited"
    },
    {
      feature: "EW Resilience",
      wolfEye: "Enhanced",
      competitorA: "Standard",
      competitorB: "Basic"
    },
    {
      feature: "Reconfiguration",
      wolfEye: "~30 min",
      competitorA: "Not modular",
      competitorB: "Hours"
    }]

  },

  gallery: [
  {
    url: "https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/fmljrabt_Wolf%20Eye%20LOGO.png",
    caption: "Wolf Eye - Modular Defense Intelligence"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/sphp7fxv_Drone%20image%201%23.png",
    caption: "Interceptor Drone Configuration"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_wolf-eye/artifacts/6p41ulha_Drone%20schematic%20from%20above.png",
    caption: "Drone Technical Schematic - Top View"
  },
  {
    url: "https://images.unsplash.com/photo-1473445730015-841f29a9490b?w=800&h=600&fit=crop",
    caption: "Deployment Module Configuration"
  }],


  pricing: {
    droneUnit: "~€8,000 (excluding payloads)",
    moduleWith9Drones: "~€90,000",
    cityScaleDeployment: "€4.5M - €13.5M (50-150 modules)",
    infrastructureProtection: "€450K - €1.8M (5-20 modules)"
  }
};

export const companyData = {
  name: "Labana Dynamics OÜ",
  founded: "2024",
  headquarters: "Tallinn, Estonia",
  mission: "Developing autonomous, modular defense systems to counter emerging aerial and ground threats with scalable, cost-effective solutions for critical infrastructure and urban protection.",

  story: "Labana Dynamics OÜ was founded by defense technology specialists and AI engineers who recognized the urgent need for affordable, rapidly deployable counter-drone and ground threat systems. Our Wolf Eye platform represents a paradigm shift from traditional fixed defense installations to modular, autonomous networks that can scale from single facilities to entire cities. With proven dual-use applications in both defense and civilian infrastructure protection, we're pioneering the next generation of intelligent security systems.",

  values: [
  {
    title: "Modularity First",
    description: "Our standardized, reconfigurable architecture enables rapid deployment, easy maintenance, and scalable protection from 5 modules to 150+ module networks."
  },
  {
    title: "Autonomous Intelligence",
    description: "AI-driven decision-making ensures operational continuity even under degraded communications and electronic warfare conditions."
  },
  {
    title: "Cost-Effectiveness",
    description: "At €90,000 per module, Wolf Eye delivers enterprise-grade protection at a fraction of traditional defense system costs, democratizing advanced security."
  },
  {
    title: "Dual-Use Innovation",
    description: "Designed for both military and civilian applications, our technology protects critical infrastructure, urban areas, and strategic installations worldwide."
  }],


  stats: [
  { value: "€90K", label: "Module Price" },
  { value: "6-9", label: "Drones Per Module" },
  { value: "<30min", label: "Deployment Time" },
  { value: "24/7", label: "Autonomous Operation" }],


  team: {
    leadership: "Our leadership team combines expertise in defense systems engineering, artificial intelligence, electronic warfare, and autonomous systems development. With backgrounds spanning NATO defense programs, unmanned systems, and AI research, we bring proven experience in delivering mission-critical technology.",
    expertise: "Specializing in modular defense architecture, AI-driven autonomy, counter-UAS systems, and electronic warfare resilience. Our team has deployed security solutions across Europe with ongoing pilot programs in high-threat environments."
  }
};

export const contactInfo = {
  email: "info@labanadynamics.com",
  phone: "+370 669 23664",
  address: "Tallinn, Estonia & \u0160iauliai, Lithuania",
  businessHours: "Monday - Friday: 10:00 - 00:00 GMT+2"
};